import type { Skill, Plugin } from '@/contexts/SkillsContext';

/**
 * Built-in skills that are hardcoded into the application
 */
export const BUILTIN_SKILLS: Plugin[] = [
  {
    id: 'builtin-stripe-payments',
    name: 'Stripe Payments',
    url: '',
    enabled: false,
    isDefault: true,
    isBuiltIn: true,
    skills: [
      {
        name: 'stripe-payments',
        description: 'Set up Stripe-based payment processing for one-time payments, subscriptions, and checkout flows using secure serverless functions and frontend components. Use when adding monetization or billing infrastructure.',
        content: `# Stripe Payments

Set up payment processing in your project using Stripe. This skill helps you add one-time payments, subscriptions, and checkout flows with secure serverless functions.

## What This Skill Does

When you ask to add Stripe payments, the AI will:

1. **Check Stripe Integration**: Verify that you've connected your Stripe account in Settings > Integrations
2. **Create Products & Prices**: Set up products and pricing in your Stripe account
3. **Generate Serverless Functions**: Create secure backend functions for payment processing
4. **Add Frontend Components**: Build payment forms, checkout buttons, or subscription UI
5. **Configure Environment Variables**: Set up proper configuration for deployment

## Example Prompts

Try these prompts to add payments to your project:

### Pricing Tiers
- "Create 3 pricing tiers at $5.99, $10.99, and $15.99"
- "Add Basic, Pro, and Enterprise plans at $9, $29, and $99 per month"

### One-Time Payments
- "Add a checkout button for a $29 product"
- "Create a payment form for a $49.99 course"
- "Add a 'Buy Now' button that charges $19.99"

### Subscriptions
- "Set up monthly subscription billing at $9.99/month"
- "Create annual subscription for $99/year"
- "Add a subscription page with monthly and yearly options"

### Payment Forms
- "Create a payment form with card input"
- "Add a custom donation form with Stripe"
- "Build a checkout page with Stripe Elements"

## Requirements

Before using this skill, make sure you:

1. **Connect Stripe**: Go to Settings > Integrations and connect your Stripe account
2. **Choose Deployment Platform**: Decide whether you'll deploy to Netlify or Vercel (the AI will ask if not specified)

## What Gets Created

The skill will create:

- **Products in Stripe**: Your pricing tiers are created in your actual Stripe account
- **Serverless Functions**: Secure backend handlers for payments (in \`netlify/functions/\` or \`api/\`)
- **Frontend Components**: React components with Stripe integration
- **Configuration Files**: \`.env.example\` with required environment variables
- **Documentation**: \`STRIPE_SETUP.md\` with deployment instructions

## Deployment

After the AI sets up Stripe, you'll need to:

1. **Deploy your project** to Netlify or Vercel
2. **Add environment variables** in your deployment platform:
   - \`VITE_STRIPE_PUBLISHABLE_KEY\` - Already shown in the setup
   - \`STRIPE_SECRET_KEY\` - Get from your Stripe Dashboard

The AI will provide the exact values you need to paste.

## Security

All payment processing happens securely:

- Secret keys are never exposed in frontend code
- Payment processing runs in serverless functions
- Customer card details never touch your servers (handled by Stripe)
- Webhook signatures verify event authenticity

## Example Workflow

**You:** "Create 3 pricing tiers: Starter at $9/month, Pro at $29/month, and Enterprise at $99/month"

**AI will:**
1. Use the \`stripe_setup\` tool to create products in your Stripe account
2. Generate serverless functions for subscription management
3. Create a pricing page component with the three tiers
4. Add subscription checkout functionality
5. Provide deployment instructions

**Result:** A complete subscription billing system ready to deploy!

## Troubleshooting

**Error: "No Stripe integration found"**
- Go to Settings > Integrations and connect your Stripe account first

**Error: "Platform not specified"**
- Tell the AI which platform you're using: "Use Netlify" or "Use Vercel"

**Payments not working after deployment**
- Check that you added the environment variables in your deployment platform
- Verify you're using the correct keys (test vs live mode)
- Check your deployment platform's function logs

## Learn More

- [Stripe Documentation](https://stripe.com/docs)
- [Stripe Testing Guide](https://stripe.com/docs/testing)
- Settings > Integrations - Connect your account`,
      },
    ],
  },
];

/**
 * Default plugin URLs that are included by default
 */
export const DEFAULT_PLUGIN_URLS = [
  'https://github.com/obra/superpowers',
] as const;

/**
 * Parse SKILL.md frontmatter and content
 */
export function parseSkillMd(content: string): Omit<Skill, 'name' | 'content'> & { name?: string } {
  // Try to match YAML frontmatter format (---...---)
  const yamlFrontmatterMatch = content.match(/^---\n([\s\S]*?)\n---/);

  let frontmatter: string;
  let body: string;
  let isTabFormat = false;

  if (yamlFrontmatterMatch) {
    // YAML frontmatter format
    frontmatter = yamlFrontmatterMatch[1];
    body = content.slice(yamlFrontmatterMatch[0].length).trim();
  } else {
    // Try tab-separated format (first two lines: headers and values)
    const lines = content.split('\n');
    if (lines.length >= 2) {
      const headers = lines[0].split('\t').map(h => h.trim()).filter(h => h);
      const values = lines[1].split('\t').map(v => v.trim());

      // Check if this looks like a TSV header
      if (headers.includes('name') || headers.includes('description')) {
        isTabFormat = true;
        // Convert TSV to frontmatter-like format
        const frontmatterLines: string[] = [];
        for (let i = 0; i < headers.length && i < values.length; i++) {
          if (headers[i] && values[i]) {
            frontmatterLines.push(`${headers[i]}: ${values[i]}`);
          }
        }
        frontmatter = frontmatterLines.join('\n');
        // Skip the header and values lines, and any blank lines before content
        let bodyStartIndex = 2;
        while (bodyStartIndex < lines.length && !lines[bodyStartIndex].trim()) {
          bodyStartIndex++;
        }
        body = lines.slice(bodyStartIndex).join('\n').trim();
      } else {
        throw new Error('SKILL.md must start with YAML frontmatter (---...---) or tab-separated format');
      }
    } else {
      throw new Error('SKILL.md must start with YAML frontmatter (---...---) or tab-separated format');
    }
  }

  // Parse frontmatter (simple parser for common cases)
  const lines = frontmatter.split('\n');
  const result: Record<string, string | string[] | Record<string, string>> = {};

  for (const line of lines) {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;

    const key = line.slice(0, colonIndex).trim();
    let value = line.slice(colonIndex + 1).trim();

    // Handle arrays (simple case)
    if (value.startsWith('[') && value.endsWith(']')) {
      value = value.slice(1, -1);
      result[key] = value.split(',').map(s => s.trim().replace(/^["']|["']$/g, ''));
    } else {
      // Remove quotes if present
      result[key] = value.replace(/^["']|["']$/g, '');
    }
  }

  if (!result['description']) {
    throw new Error('SKILL.md frontmatter must include a description');
  }

  return {
    name: result['name'] as string | undefined,
    description: result['description'] as string,
    license: result['license'] as string | undefined,
    allowedTools: result['allowed-tools'] as string[] | undefined,
    metadata: result['metadata'] as Record<string, string> | undefined,
    content: body,
  };
}

/**
 * Extract GitHub/GitLab raw content URL from repository URL
 */
export function getSkillRawUrl(repoUrl: string, skillPath: string): string {
  // Normalize URL
  let url = repoUrl.replace(/\.git$/, '').replace(/\/$/, '');

  // Handle GitHub
  if (url.includes('github.com')) {
    // https://github.com/owner/repo -> https://raw.githubusercontent.com/owner/repo/main/
    const match = url.match(/github\.com\/([^/]+)\/([^/]+)/);
    if (match) {
      return `https://raw.githubusercontent.com/${match[1]}/${match[2]}/main/${skillPath}`;
    }
  }

  // Handle GitLab
  if (url.includes('gitlab.com')) {
    // https://gitlab.com/owner/repo -> https://gitlab.com/owner/repo/-/raw/main/
    const match = url.match(/gitlab\.com\/([^/]+)\/([^/]+)/);
    if (match) {
      return `https://gitlab.com/${match[1]}/${match[2]}/-/raw/main/${skillPath}`;
    }
  }

  // Generic fallback - assume it's a direct URL
  return `${url}/${skillPath}`;
}

/**
 * Fetch skills from a repository
 */
export async function fetchPluginSkills(
  pluginUrl: string,
  corsProxy?: string
): Promise<{ name: string; skills: Skill[] }> {
  const skills: Skill[] = [];

  // Normalize URL
  const url = pluginUrl.replace(/\.git$/, '').replace(/\/$/, '');

  // Extract repo name for plugin name
  const repoNameMatch = url.match(/\/([^/]+)$/);
  const pluginName = repoNameMatch ? repoNameMatch[1] : 'unknown';

  // Try to fetch the repository contents
  let contentsUrl: string;
  let isGitHub = false;
  let isGitLab = false;

  if (url.includes('github.com')) {
    // GitHub API
    const match = url.match(/github\.com\/([^/]+)\/([^/]+)/);
    if (match) {
      contentsUrl = `https://api.github.com/repos/${match[1]}/${match[2]}/contents`;
      isGitHub = true;
    } else {
      throw new Error('Invalid GitHub URL');
    }
  } else if (url.includes('gitlab.com')) {
    // GitLab API
    const match = url.match(/gitlab\.com\/([^/]+)\/([^/]+)/);
    if (match) {
      const projectId = encodeURIComponent(`${match[1]}/${match[2]}`);
      contentsUrl = `https://gitlab.com/api/v4/projects/${projectId}/repository/tree`;
      isGitLab = true;
    } else {
      throw new Error('Invalid GitLab URL');
    }
  } else {
    throw new Error('Only GitHub and GitLab repositories are supported');
  }

  // Apply CORS proxy if needed
  const fetchUrl = corsProxy
    ? corsProxy.replace('{href}', encodeURIComponent(contentsUrl))
    : contentsUrl;

  const response = await fetch(fetchUrl, {
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch repository contents: ${response.statusText}`);
  }

  const contents = await response.json();

  // Find directories that might contain skills
  const directories: string[] = [];
  let hasSkillsDir = false;

  if (isGitHub) {
    for (const item of contents) {
      if (item.type === 'dir') {
        directories.push(item.name);
        if (item.name === 'skills') {
          hasSkillsDir = true;
        }
      }
    }
  } else if (isGitLab) {
    for (const item of contents) {
      if (item.type === 'tree') {
        directories.push(item.name);
        if (item.name === 'skills') {
          hasSkillsDir = true;
        }
      }
    }
  }

  // If there's a skills/ directory, fetch its contents and look for skills there
  if (hasSkillsDir) {
    try {
      let skillsDirUrl: string;

      if (isGitHub) {
        skillsDirUrl = `${contentsUrl}/skills`;
      } else {
        // GitLab
        skillsDirUrl = `${contentsUrl}?path=skills`;
      }

      const skillsDirFetchUrl = corsProxy
        ? corsProxy.replace('{href}', encodeURIComponent(skillsDirUrl))
        : skillsDirUrl;

      const skillsDirResponse = await fetch(skillsDirFetchUrl, {
        headers: { 'Accept': 'application/json' },
      });

      if (skillsDirResponse.ok) {
        const skillsDirContents = await skillsDirResponse.json();

        // Get skill directories inside skills/
        const skillSubdirs: string[] = [];
        if (isGitHub) {
          for (const item of skillsDirContents) {
            if (item.type === 'dir') {
              skillSubdirs.push(item.name);
            }
          }
        } else if (isGitLab) {
          for (const item of skillsDirContents) {
            if (item.type === 'tree') {
              skillSubdirs.push(item.name);
            }
          }
        }

        // Check each subdirectory in skills/ for SKILL.md
        for (const subdir of skillSubdirs) {
          try {
            const skillMdPath = `skills/${subdir}/SKILL.md`;
            const rawUrl = getSkillRawUrl(url, skillMdPath);

            const skillFetchUrl = corsProxy
              ? corsProxy.replace('{href}', encodeURIComponent(rawUrl))
              : rawUrl;

            const skillResponse = await fetch(skillFetchUrl);

            if (skillResponse.ok) {
              const skillContent = await skillResponse.text();
              const parsed = parseSkillMd(skillContent);

              const skillName = parsed.name || subdir;

              skills.push({
                name: skillName,
                description: parsed.description,
                license: parsed.license,
                allowedTools: parsed.allowedTools,
                metadata: parsed.metadata,
                content: parsed.content,
              });
            }
          } catch (error) {
            console.debug(`No skill found in skills/${subdir}:`, error);
          }
        }
      }
    } catch (error) {
      console.debug('Failed to fetch skills/ directory:', error);
    }
  }

  // Also check root-level directories for SKILL.md (excluding 'skills' dir)
  for (const dir of directories) {
    if (dir === 'skills') continue; // Already handled above

    try {
      const skillMdPath = `${dir}/SKILL.md`;
      const rawUrl = getSkillRawUrl(url, skillMdPath);

      const skillFetchUrl = corsProxy
        ? corsProxy.replace('{href}', encodeURIComponent(rawUrl))
        : rawUrl;

      const skillResponse = await fetch(skillFetchUrl);

      if (skillResponse.ok) {
        const skillContent = await skillResponse.text();
        const parsed = parseSkillMd(skillContent);

        // Use directory name as skill name if not specified
        const skillName = parsed.name || dir;

        skills.push({
          name: skillName,
          description: parsed.description,
          license: parsed.license,
          allowedTools: parsed.allowedTools,
          metadata: parsed.metadata,
          content: parsed.content,
        });
      }
    } catch (error) {
      // Skill doesn't exist in this directory, continue
      console.debug(`No skill found in ${dir}:`, error);
    }
  }

  // Also check for a root-level SKILL.md (for single-skill repos)
  try {
    const rootSkillUrl = getSkillRawUrl(url, 'SKILL.md');
    const skillFetchUrl = corsProxy
      ? corsProxy.replace('{href}', encodeURIComponent(rootSkillUrl))
      : rootSkillUrl;

    const rootResponse = await fetch(skillFetchUrl);

    if (rootResponse.ok) {
      const skillContent = await rootResponse.text();
      const parsed = parseSkillMd(skillContent);

      skills.push({
        name: parsed.name || pluginName,
        description: parsed.description,
        license: parsed.license,
        allowedTools: parsed.allowedTools,
        metadata: parsed.metadata,
        content: parsed.content,
      });
    }
  } catch {
    // No root-level skill
  }

  return { name: pluginName, skills };
}

/**
 * Generate a unique plugin ID from URL
 */
export function generatePluginId(url: string): string {
  // Extract owner/repo from URL
  const match = url.match(/(?:github\.com|gitlab\.com)\/([^/]+)\/([^/]+)/);
  if (match) {
    return `${match[1]}-${match[2]}`.toLowerCase();
  }
  // Fallback to hash
  return btoa(url).replace(/[^a-zA-Z0-9]/g, '').slice(0, 16);
}

/**
 * Create a plugin from URL
 */
export async function createPluginFromUrl(
  url: string,
  corsProxy?: string
): Promise<Plugin> {
  const { name, skills } = await fetchPluginSkills(url, corsProxy);

  if (skills.length === 0) {
    throw new Error('No skills found in repository. Skills must have a SKILL.md file with valid frontmatter.');
  }

  return {
    id: generatePluginId(url),
    name,
    url,
    enabled: true,
    skills,
    lastFetched: Date.now(),
  };
}
